import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const TARGET = 1080 / 2340;
const TOLERANCE = 0.04;   // must match the Lobby countdown shape guard
const BE = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? (u.startsWith("http") ? u : `${BE}${u}`) : null);
const cache = {};

// Measures each campaign's takeover image -> { [id]: { ok, w, h } } (missing = no takeover).
export function useTakeoverStatus(rows, idKey) {
  const [status, setStatus] = useState({});
  const sig = rows.map((c) => `${c[idKey]}:${c.creatives?.feature || ""}`).join("|");
  useEffect(() => {
    let alive = true;
    rows.forEach((c) => {
      const url = abs(c.creatives?.feature);
      if (!url) return;
      const done = (r) => alive && setStatus((s) => ({ ...s, [c[idKey]]: r }));
      if (cache[url]) return done(cache[url]);
      const im = new Image();
      im.onload = () => { cache[url] = { ok: Math.abs(im.naturalWidth / im.naturalHeight - TARGET) <= TOLERANCE, w: im.naturalWidth, h: im.naturalHeight }; done(cache[url]); };
      im.onerror = () => { cache[url] = { ok: false, w: 0, h: 0 }; done(cache[url]); };
      im.src = url;
    });
    return () => { alive = false; };
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = (c) => (c.creatives?.feature ? status[c[idKey]] : undefined);
  return { get: live, badCount: rows.filter((c) => live(c) && !live(c).ok).length };
}

export const TakeoverBadge = ({ st, id, onReplace, hint }) => {
  if (!st) return null;
  if (st.ok) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-green-500 border border-green-500/40 bg-green-500/10 rounded px-1.5 py-0.5" data-testid={`takeover-ok-${id}`}>
      <CheckCircle2 size={10} /> Takeover ready
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 max-w-full text-[10px] font-mono text-amber-500 border border-amber-500/50 bg-amber-500/10 rounded px-1.5 py-0.5" data-testid={`takeover-bad-${id}`}>
      <AlertTriangle size={11} className="shrink-0" />
      <span className="sm:truncate" title="Wrong-shaped takeovers are skipped in the 10-second countdown">Takeover {st.w ? `${st.w}×${st.h}` : "unreadable"} · not showing · needs 1080×2340</span>
      {onReplace ? (
        <button type="button" onClick={onReplace} className="underline font-semibold hover:text-amber-400 shrink-0" data-testid={`takeover-replace-${id}`}>Replace</button>
      ) : hint ? <span className="text-race-sub truncate" title={hint}>{hint}</span> : null}
    </span>
  );
};

export const TakeoverSummary = ({ count }) => (count > 0 ? (
  <div className="flex items-center gap-2 text-xs text-amber-500 border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2" data-testid="takeover-summary">
    <AlertTriangle size={14} />
    <span><b>{count}</b> campaign{count === 1 ? "" : "s"} need{count === 1 ? "s" : ""} a new takeover - wrong-shaped images are skipped in the 10-second countdown.</span>
  </div>
) : null);
